import type { SmartCreationBrowserDraft } from "@adpilot/shared";
import { type RefObject, useEffect, useRef, useState } from "react";
import { hasLocalSmartCreationReviewEdits } from "@/lib/smart-creation-draft-store";
import { usePlanIdentityReset } from "./use-plan-identity-reset";

/**
 * Asks before any command regenerates the preview. Regeneration starts from the signed
 * generation, so saved Review edits would be lost; every pending prompt resolves `false`
 * when the Plan changes or the workspace unmounts.
 */
export function usePreviewRegeneration(
  identity: string,
  browserDraftRef: RefObject<SmartCreationBrowserDraft | null>,
) {
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const regenerateResolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  usePlanIdentityReset(identity, () => {
    regenerateResolverRef.current?.(false);
    regenerateResolverRef.current = null;
    setRegenerateDialogOpen(false);
  });
  useEffect(() => () => regenerateResolverRef.current?.(false), []);

  const settlePreviewRegeneration = (confirmed: boolean) => {
    const resolve = regenerateResolverRef.current;
    regenerateResolverRef.current = null;
    setRegenerateDialogOpen(false);
    resolve?.(confirmed);
  };

  const confirmPreviewRegeneration = (): Promise<boolean> => {
    const current = browserDraftRef.current;
    if (
      current?.draft.currentStep !== "preview" ||
      !hasLocalSmartCreationReviewEdits(current.id, current.shopId)
    )
      return Promise.resolve(true);
    regenerateResolverRef.current?.(false);
    return new Promise((resolve) => {
      regenerateResolverRef.current = resolve;
      setRegenerateDialogOpen(true);
    });
  };

  return { regenerateDialogOpen, settlePreviewRegeneration, confirmPreviewRegeneration };
}
